/**
 * Counter-backed suppression for status-0 callable failures.
 *
 * THE PROBLEM. A status-0 network failure means there was no HTTP response at
 * all: the tab unloaded, the radio died, the device suspended, the route to the
 * host broke. Sampling against the server's own logs (#4761) found the server
 * had received and 200'd almost all of them. In `error_groups` each one is
 * fingerprinted per callable, so ambient field flakiness mints a NEW group per
 * callable — measured 2026-09-21: 24 such groups over 30 days across 18
 * callables, each needing its own dismissal, none of them a defect.
 *
 * 🔴 THE RULE: SUPPRESS THE EVENT, NEVER THE FACT. A blanket ignore would make
 * "our callables are unreachable" look identical to "nothing happened" — the
 * exact failure this module's neighbours (draftSyncNetworkPolicy,
 * deliveryFailures) exist to prevent. So every drop is COUNTED, per callable,
 * per UTC day, and a BURST escalates into one loud report that names the
 * callable and carries the running totals.
 *
 * WHAT A REAL OUTAGE LOOKS LIKE, AND WHY THIS SEES IT. A dropped IAM invoker
 * binding or a CORS regression makes ONE callable fail back-to-back for EVERY
 * user who touches it. Each affected browser trips the burst threshold within
 * seconds and emits one report under a per-callable fingerprint, so the group's
 * occurrence count and distinct-user count climb immediately — many users, one
 * callable, short window, which is the shape being looked for. Ambient flakiness
 * is the opposite shape: one user, scattered callables, spread over a day.
 *
 * 🔴 WHAT THIS DOES NOT SEE. This counter is a per-browser-tab in-memory
 * singleton — it has no cross-user aggregation of its own. The burst path only
 * fires when a SINGLE tab racks up 3 status-0 failures on the same callable
 * within the 10-minute window. A brief, widespread outage where each affected
 * user's tab only touches the callable once or twice before it recovers never
 * reaches 3-in-one-tab, so no tab escalates and the loud `error_groups` ->
 * Chat alert never fires for that shape — even though every user was affected.
 * Every one of those events still reaches Sentry regardless (captureError.ts:
 * `Sentry.captureException` runs before the `skipErrorGroups` early-return, so
 * delivery to Sentry is unconditional even when the `error_groups` write is
 * skipped — verified against source 2026-09-21). Sentry's host-level transport
 * bucket (grouped by `network_endpoint` tag, not per-tab) is therefore the
 * ONLY cross-user signal for a brief-widespread-outage shape; this counter's
 * loud alarm is a same-tab detector, not a full substitute for one.
 *
 * THE THRESHOLD IS MEASURED, NOT PICKED (read-only query of `error_events`,
 * 2026-09-21):
 *   - 54 status-0 callable events in 30 days, over 18 callables and 16 users.
 *   - Grouped into bursts (same user + same callable, gap <= 10 min): 48
 *     bursts — 42 of size 1, 6 of size 2. **The largest burst in 30 days of
 *     ambient noise was 2.**
 *   - Per (user, callable, UTC day): 36 buckets at 1, three at 2, one at 4 and
 *     one at 8 — the larger two spread across a whole day, not a window.
 * A threshold of 3-within-10-minutes therefore has a MEASURED false-positive
 * rate of 0/48 bursts over 30 days. The sample is the same population this
 * counter sees (events that already survived the unload / offline /
 * device-sleep filters ahead of it), and since those filters only shipped at
 * D48 the sample is a SUPERSET of what will now flow — the threshold errs
 * conservative, not optimistic.
 *
 * Re-derive rather than trust those numbers: they come from `error_events`,
 * whose retention is ~90 days. If the ambient burst maximum ever reaches 3, the
 * threshold is wrong and this comment is the place that says so.
 */

/** Failures on one callable within the window before we escalate. Measured above. */
export const TRANSPORT_BURST_THRESHOLD = 3;

/** The rolling window a burst must fit inside, and the post-escalation cooldown. */
export const TRANSPORT_BURST_WINDOW_MS = 10 * 60_000;

interface EndpointState {
  /** Timestamps inside the rolling window. Pruned on every record. */
  recent: number[];
  /** Every failure today, including the ones already pruned from `recent`. */
  dayCount: number;
  /** UTC date key the day count belongs to. */
  day: string;
  /** When we last escalated, for the cooldown. */
  lastEscalatedAt: number | null;
}

const endpoints = new Map<string, EndpointState>();
let totalSuppressed = 0;

function utcDay(now: number): string {
  return new Date(now).toISOString().slice(0, 10);
}

export interface TransportFailureOutcome {
  /** True when this failure should be reported loudly instead of only counted. */
  readonly escalate: boolean;
  /** Failures on this callable inside the rolling window, including this one. */
  readonly windowCount: number;
  /** Failures on this callable so far today (UTC), including this one. */
  readonly dayCount: number;
}

/**
 * Record one suppressed status-0 failure and say whether it is a burst.
 *
 * `now` is a parameter rather than a `Date.now()` call so the thresholds are
 * testable at exact boundaries — a timer-mocked test of a rolling window is the
 * kind that passes for the wrong reason.
 */
export function recordTransportFailure(
  endpoint: string,
  now: number = Date.now()
): TransportFailureOutcome {
  const day = utcDay(now);
  const existing = endpoints.get(endpoint);
  const state: EndpointState = existing ?? {
    recent: [],
    dayCount: 0,
    day,
    lastEscalatedAt: null,
  };

  // The day tally rolls at UTC midnight. The WINDOW deliberately does not: an
  // outage that starts at 23:58 is still one outage at 00:01, and resetting the
  // burst at midnight would blind us for the first minutes of a new day.
  if (state.day !== day) {
    state.day = day;
    state.dayCount = 0;
  }

  state.recent = state.recent.filter((t) => now - t < TRANSPORT_BURST_WINDOW_MS);
  state.recent.push(now);
  state.dayCount += 1;
  totalSuppressed += 1;

  const windowCount = state.recent.length;
  const coolingDown =
    state.lastEscalatedAt !== null &&
    now - state.lastEscalatedAt < TRANSPORT_BURST_WINDOW_MS;
  const escalate = windowCount >= TRANSPORT_BURST_THRESHOLD && !coolingDown;

  if (escalate) {
    state.lastEscalatedAt = now;
    // Start the next burst from scratch, so a sustained outage reports once per
    // cooldown on fresh evidence rather than re-reporting the same three hits.
    state.recent = [];
  }

  endpoints.set(endpoint, state);

  return { escalate, windowCount, dayCount: state.dayCount };
}

export interface TransportFailureStats {
  /** Every status-0 callable failure this tab has suppressed, all endpoints. */
  readonly totalSuppressed: number;
  readonly byEndpoint: Readonly<Record<string, { dayCount: number; windowCount: number }>>;
}

/**
 * The counters, for anything that wants to read them (a debug panel, a test, a
 * future health surface).
 *
 * This export is not decoration: a count nobody can read is indistinguishable
 * from a drop, which is the failure this whole module exists to avoid.
 */
export function getTransportFailureStats(): TransportFailureStats {
  const byEndpoint: Record<string, { dayCount: number; windowCount: number }> = {};
  endpoints.forEach((state, endpoint) => {
    byEndpoint[endpoint] = { dayCount: state.dayCount, windowCount: state.recent.length };
  });
  return { totalSuppressed, byEndpoint };
}

/** Tests only — module state is a singleton like every other counter here. */
export function __resetTransportFailureCounterForTests(): void {
  endpoints.clear();
  totalSuppressed = 0;
}
